import React, { useState } from 'react';
import { ServiceName, validateServiceConfigData } from 'data/terra/ServiceImpl';
import { Switch, Typography } from 'antd';
import {
  ServiceConfigDescription,
  ServiceConfigIntegrationDescription,
} from 'data/terra/ServiceConfig';
import ReactJson, { InteractionProps } from 'react-json-view';
import LabelledInput from 'components/LabelledInput';
import { t } from 'helpers/i18n';
import { UndefinedAble } from 'data/base';
import { isEmpty } from 'lodash';

export interface ServiceJsonConfigBoardProps {
  serviceName: ServiceName;
  serviceConfig: ServiceConfigDescription<any>;
  onToggleService: (checked: boolean) => void;
  onEditServiceConfig: (
    data: ServiceConfigDescription<any>,
    isValid: boolean
  ) => void;
  showEnabled: boolean;
}

function ServiceJsonConfigBoard(props: ServiceJsonConfigBoardProps) {
  const [errorString, setErrorString] = useState<string>('');
  const [jsonExtraConfigText, setConfigText] = useState<UndefinedAble<string>>(
    props.serviceConfig?.data
      ? JSON.stringify(props.serviceConfig?.data)
      : undefined
  );

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (jsonExtraConfigText !== event.target.value) {
      setConfigText(event.target.value);

      try {
        const parsed = JSON.parse(event.target.value);
        const isValid = validateConfig(parsed);

        props.onEditServiceConfig(
          {
            ...props.serviceConfig,
            data: parsed,
          },
          isValid
        );
      } catch (e) {
        setErrorString(t('Invalid JSON'));
        props.onEditServiceConfig(props.serviceConfig, false);
      }
    }
  };

  const onToggleService = (checked: boolean) => {
    props.onToggleService(checked);
  };

  const onEditConfigData = (value: object): Boolean => {
    const isValid = validateConfig(value);

    setConfigText(JSON.stringify(value));
    props.onEditServiceConfig(
      {
        ...props.serviceConfig,
        data: value,
      },
      isValid
    );

    return isValid;
  };

  const validateConfig = (value: any): boolean => {
    const validate = validateServiceConfigData(props.serviceName, value);
    if (validate.isValid) {
      setErrorString('');
      return true;
    } else {
      setErrorString(validate.error);
      return false;
    }
  };

  return (
    <div>
      {props.showEnabled && (
        <Switch
          checked={
            (props.serviceConfig as ServiceConfigIntegrationDescription<any>)
              ?.isActive
          }
          onChange={onToggleService}
          style={{ marginTop: '8px', marginBottom: '8px' }}
        />
      )}
      {!props.showEnabled && (
        <div style={{ marginTop: '8px', marginBottom: '8px' }} />
      )}
      <LabelledInput
        label={t('Enter custom JSON configuration')}
        helperText={t('Enter custom JSON configuration')}
        value={jsonExtraConfigText}
        onChange={onTextChange}
        marginBottom={true}
        mode="vertical"
      />
      {jsonExtraConfigText && errorString && (
        <Typography.Text style={{ color: 'red' }}>
          {errorString}
        </Typography.Text>
      )}
      {jsonExtraConfigText && !isEmpty(props.serviceConfig?.data) && (
        <ReactJson
          src={props.serviceConfig?.data}
          name={null}
          iconStyle="circle"
          displayDataTypes={false}
          displayObjectSize={true}
          collapseStringsAfterLength={50}
          enableClipboard={true}
          onAdd={(add: InteractionProps) => {
            return onEditConfigData(add.updated_src);
          }}
          onDelete={(del: InteractionProps) => {
            return onEditConfigData(del.updated_src);
          }}
          onEdit={(edit: InteractionProps) => {
            return onEditConfigData(edit.updated_src);
          }}
        />
      )}
    </div>
  );
}

export default ServiceJsonConfigBoard;
